/**
 * TanStack Query Hooks for CloudLedger API
 *
 * Provides React hooks wrapping the RPC client.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createFullClient } from "./api";
import { useAuth } from "../hooks/useAuth";
import { useAIKey } from "../hooks/useAIKey";

// ============================================================================
// Query Keys
// ============================================================================

export const queryKeys = {
    transactions: (ledgerId: string) => ["transactions", ledgerId] as const,
    transaction: (id: string) => ["transaction", id] as const,
    categories: () => ["categories"] as const,
    ledgers: () => ["ledgers"] as const,
    health: () => ["health"] as const,
} as const;

// ============================================================================
// Transaction Hooks
// ============================================================================

interface UseTransactionsParams {
    ledgerId: string;
    page?: number;
    limit?: number;
}

/**
 * Hook to fetch paginated transactions
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useTransactions({
 *   ledgerId: "uuid",
 *   page: 1,
 *   limit: 20
 * });
 * ```
 */
export const useTransactions = ({
    ledgerId,
    page = 1,
    limit = 20,
}: UseTransactionsParams) => {
    const { token } = useAuth();
    const { aiKey } = useAIKey();

    return useQuery({
        queryKey: [...queryKeys.transactions(ledgerId), page, limit],
        queryFn: async () => {
            if (!token) throw new Error("Not authenticated");

            const client = createFullClient(token, aiKey ?? undefined);
            const response = await client.api.transactions.$get({
                query: {
                    ledgerId,
                    page: page.toString(),
                    limit: limit.toString(),
                },
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(
                    (error as { error?: string }).error || "Failed to fetch transactions"
                );
            }

            return response.json();
        },
        enabled: !!token && !!ledgerId,
        staleTime: 1000 * 60, // 1 minute
    });
};

interface CreateTransactionParams {
    ledgerId: string;
    categoryId: string;
    amount: number;
    date: Date;
    note?: string;
}

/**
 * Mutation hook to create a transaction
 *
 * @example
 * ```tsx
 * const mutation = useCreateTransaction();
 * mutation.mutate({
 *   ledgerId: "uuid",
 *   categoryId: "uuid",
 *   amount: 10050, // cents
 *   date: new Date(),
 *   note: "Lunch"
 * });
 * ```
 */
export const useCreateTransaction = () => {
    const queryClient = useQueryClient();
    const { token } = useAuth();
    const { aiKey } = useAIKey();

    return useMutation({
        mutationFn: async (params: CreateTransactionParams) => {
            if (!token) throw new Error("Not authenticated");

            const client = createFullClient(token, aiKey ?? undefined);
            const response = await client.api.transactions.$post({
                json: {
                    ledgerId: params.ledgerId,
                    categoryId: params.categoryId,
                    amount: params.amount,
                    date: params.date.toISOString(),
                    note: params.note,
                },
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(
                    (error as { error?: string }).error || "Failed to create transaction"
                );
            }

            return response.json();
        },
        onSuccess: (_, variables) => {
            // Invalidate transactions list for this ledger
            queryClient.invalidateQueries({
                queryKey: queryKeys.transactions(variables.ledgerId),
            });
        },
    });
};

/**
 * Mutation hook to delete a transaction
 */
export const useDeleteTransaction = () => {
    const queryClient = useQueryClient();
    const { token } = useAuth();

    return useMutation({
        mutationFn: async ({
            id,
            ledgerId,
        }: {
            id: string;
            ledgerId: string;
        }) => {
            if (!token) throw new Error("Not authenticated");

            const client = createFullClient(token);
            const response = await client.api.transactions[":id"].$delete({
                param: { id },
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(
                    (error as { error?: string }).error || "Failed to delete transaction"
                );
            }

            return response.json();
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.transactions(variables.ledgerId),
            });
        },
    });
};

// ============================================================================
// AI Hooks
// ============================================================================

interface SuggestCategoryParams {
    note: string;
}

/**
 * Mutation hook for AI category suggestion
 *
 * @example
 * ```tsx
 * const mutation = useSuggestCategory();
 * const suggestions = await mutation.mutateAsync({ note: "Coffee at Starbucks" });
 * ```
 */
export const useSuggestCategory = () => {
    const { token } = useAuth();
    const { aiKey, apiBaseUrl, chatModel, embeddingModel } = useAIKey();

    return useMutation({
        mutationFn: async ({ note }: SuggestCategoryParams) => {
            if (!token) throw new Error("Not authenticated");
            if (!aiKey) throw new Error("AI API key not configured");

            // Pass all AI config to include custom headers (baseURL, models)
            const client = createFullClient(
                token,
                aiKey,
                apiBaseUrl || undefined,
                chatModel || undefined,
                embeddingModel || undefined
            );
            const response = await client.api.ai["suggest-category"].$post({
                json: { note },
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(
                    (error as { error?: string }).error || "Failed to get suggestions"
                );
            }

            return response.json();
        },
    });
};

interface TextToSQLParams {
    query: string;
    execute?: boolean;
}

/**
 * Mutation hook for Text-to-SQL
 */
export const useTextToSQL = () => {
    const { token } = useAuth();
    const { aiKey, apiBaseUrl, chatModel, embeddingModel } = useAIKey();

    return useMutation({
        mutationFn: async ({ query, execute = false }: TextToSQLParams) => {
            if (!token) throw new Error("Not authenticated");
            if (!aiKey) throw new Error("AI API key not configured");

            // Pass all AI config to include custom headers
            const client = createFullClient(
                token,
                aiKey,
                apiBaseUrl || undefined,
                chatModel || undefined,
                embeddingModel || undefined
            );
            const response = await client.api.ai["text-to-sql"].$post({
                json: { query, execute },
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(
                    (error as { error?: string }).error || "Failed to generate SQL"
                );
            }

            return response.json();
        },
    });
};
